import { useMutation, useQuery } from '@apollo/client';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import {
  type NWTEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { useFeatures, useViewerData } from '@xing-com/hub';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconJobsFilled,
  IconMessages,
  IconMessagesFilled,
  IconNews,
  IconNewsFilled,
  IconNotificationFilled,
} from '@xing-com/icons';
import { ProductsMenuWithItemsLoggedIn } from '@xing-com/platform-layout-products-menu';
import { TipBoxPreset } from '@xing-com/tip-box';
import { Toggle } from '@xing-com/toggle';
import { spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { DiscountBadge } from './discount-badge';
import {
  StyledProductsMenuWrapper,
  BoneyardWithDiscountContainer,
} from './header.styles';
import { MeMenuDropdown } from './me-menu-dropdown';
import { NavItemHeader } from './nav-item-header';
import { NotificationCenterIcon } from './notification-center-icon';
import { SearchDropdown } from './search-dropdown';
import {
  GetSixtFocusmodeDocument,
  GetSixtFocusmodeQuery,
  UpdateSixtFocusmodeStateDocument,
} from './sixt-focusmode-state.gql-types';
import { Boneyard } from './upsell-boneyard/boneyard';

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spaceS};
  align-items: center;
`;

export const HeaderNavItemsLoggedIn: React.FC = () => {
  const { formatMessage } = useIntl();
  const { track } = useTracking<NWTEvent>();

  const userId = useViewerData()?.data?.user?.id;
  const { showUpsellHint, showJobSeekerBoneyardUpsellHint } =
    useFeatures()?.data || {};

  const [isFirstTime, setIsFirstTime] = useLocalStorage(
    'jop-focus-tip-box',
    true
  );

  const { data } = useQuery<GetSixtFocusmodeQuery>(GetSixtFocusmodeDocument);
  const sixtFocusmodeAvailable =
    data?.viewer?.coreSettings?.sixtFocusmodeAvailable?.value ?? false;
  const sixtFocusmodeState =
    data?.viewer?.coreSettings?.sixtFocusmodeState?.value ?? false;

  const [focusViewMode, setFocusViewMode] = useState(sixtFocusmodeState);

  useEffect(() => {
    setFocusViewMode(sixtFocusmodeState);
  }, [sixtFocusmodeState]);

  const isFocusViewEnabled = sixtFocusmodeAvailable && focusViewMode;
  const [mutate, { loading: mutationLoading }] = useMutation(
    UpdateSixtFocusmodeStateDocument,
    {
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'Viewer', id: userId }),
          fields: {
            coreSettings(existingCoreSettings = {}) {
              return {
                ...existingCoreSettings,
                sixtFocusmodeState:
                  data?.updateCoreSettings?.success?.sixtFocusmodeState,
              };
            },
          },
        });
      },
      onCompleted: (data) => {
        const value =
          data.updateCoreSettings?.success?.sixtFocusmodeState?.value;

        if (value !== undefined) {
          setFocusViewMode(value);

          track({
            type: 'nwt',
            event: value ? 'activated' : 'deactivated',
            eventSchema: 'basic',
            sentBy: 'navigation',
            element: 'sixt_focusmode',
            elementDetail: 'toggle',
          });
        }
      },
    }
  );

  const [showTipBox, setShowTipBox] = useState(isFirstTime);

  const handleToggleChange = (): void => {
    setShowTipBox(false);
    setFocusViewMode(!focusViewMode);

    mutate({
      variables: {
        input: {
          sixtFocusmodeState: {
            value: !focusViewMode,
          },
        },
      },
    });
  };

  const onOpenChange = (open: boolean): void => {
    setIsFirstTime(false);

    if (!open) {
      setShowTipBox(false);
    }
  };

  const onClickTipbox = (): void => {
    setIsFirstTime(false);

    handleToggleChange();
  };

  const onDisableAutofocus = (e: Event): void => {
    e.preventDefault();
  };

  return (
    <>
      <NavItemHeader
        activeItemValues={['jobs']}
        icon={IconJobs}
        activeIcon={IconJobsFilled}
        title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
        href="/jobs/find"
        tracking={{
          nwt_nav: 'find_jobs',
          sc_o: 'navigation_find_jobs',
        }}
      />
      <NavItemHeader
        activeItemValues={['my-jobs']}
        icon={IconBookmark}
        activeIcon={IconBookmarkFilled}
        title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
        href="/jobs/my-jobs"
        tracking={{
          nwt_nav: 'your_jobs',
          sc_o: 'navigation_your_jobs',
          sc_o_PropActionOrigin: [
            'navigation_badge_jobs',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      {isFocusViewEnabled ? null : (
        <NavItemHeader
          icon={IconContacts}
          activeIcon={IconContactsFilled}
          title={formatMessage({ id: 'MAINNAV_NETWORK' })}
          href="/network"
          activeItemValues={['members']}
          tracking={{
            nwt_nav: 'network',
            sc_o: 'navigation_network',
            sc_o_PropActionOrigin: [
              'navigation_badge_network',
              NAVIGATION_BADGE_NO_BADGE,
            ],
          }}
        />
      )}
      {isFocusViewEnabled ? null : (
        <NavItemHeader
          icon={IconNews}
          activeIcon={IconNewsFilled}
          title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
          href="/discover/updates"
          activeItemValues={['disco']}
          tracking={{
            nwt_nav: 'insights',
            sc_o: 'navigation_insights',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          }}
        />
      )}
      <NavItemHeader
        icon={IconMessages}
        activeIcon={IconMessagesFilled}
        title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
        href="/chats"
        activeItemValues={['messages']}
        notificationKey="unreadChats"
        tracking={{
          nwt_nav: 'messages',
          sc_o: 'navigation_messages',
          sc_o_PropActionOrigin: [
            'navigation_badge_messages',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      <NavItemHeader
        icon={NotificationCenterIcon}
        activeIcon={IconNotificationFilled}
        provideIconContext
        activeItemValues={['supi']}
        notificationKey="ncWeb"
        href="/notifications"
        title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
        tracking={{
          nwt_nav: 'notifications',
          sc_o: 'navigation_notifications',
          sc_o_PropActionOrigin: [
            'navigation_badge_notifications',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      {isFocusViewEnabled ? null : <SearchDropdown />}
      <MeMenuDropdown />
      {!showUpsellHint ? null : showJobSeekerBoneyardUpsellHint ? (
        <BoneyardWithDiscountContainer>
          <Boneyard
            dataTestId="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/projobs?reagent=uplt_277&sc_o=navigation_projobs_cta"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PROJOBS',
            })}
          </Boneyard>
          <DiscountBadge product={ProductType.Projobs} />
        </BoneyardWithDiscountContainer>
      ) : (
        <BoneyardWithDiscountContainer>
          <Boneyard
            dataTestId="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/premium?reagent=uplt_95&sc_o=navigation_premium_cta"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PREMIUM',
            })}
          </Boneyard>
          <DiscountBadge product={ProductType.Premium} />
        </BoneyardWithDiscountContainer>
      )}

      {isFocusViewEnabled ? null : (
        <StyledProductsMenuWrapper>
          <ProductsMenuWithItemsLoggedIn />
        </StyledProductsMenuWrapper>
      )}

      {sixtFocusmodeAvailable && (
        <>
          <ToggleWrapper>
            <Toggle
              disabled={mutationLoading}
              on={focusViewMode}
              onChange={handleToggleChange}
            />
            <BodyCopy size="small" noMargin>
              {formatMessage({
                id: 'MAINNAV_JOB_FOCUS_TOGGLE',
              })}
            </BodyCopy>
            <TipBoxPreset
              align="end"
              bodyText={formatMessage({
                id: sixtFocusmodeState
                  ? 'TIPBOX_JOB_FOCUS_ON_DESCRIPTION'
                  : 'TIPBOX_JOB_FOCUS_DESCRIPTION',
              })}
              buttonLabel={formatMessage({
                id: sixtFocusmodeState
                  ? 'TIPBOX_JOB_FOCUS_ON_BUTTON_LABEL'
                  : 'TIPBOX_JOB_FOCUS_BUTTON_LABEL',
              })}
              headlineText={formatMessage({
                id: sixtFocusmodeState
                  ? 'TIPBOX_JOB_FOCUS_ON_TITLE'
                  : 'TIPBOX_JOB_FOCUS_TITLE',
              })}
              {...(sixtFocusmodeState
                ? {}
                : {
                    markerText: formatMessage({
                      id: 'TIPBOX_JOB_FOCUS_MARKER',
                    }),
                  })}
              showArrow
              open={showTipBox}
              onOpenChange={onOpenChange}
              onOpenAutoFocus={onDisableAutofocus}
              buttonProps={{
                onClick: onClickTipbox,
              }}
              showClosingIndicator
              side="bottom"
              variant={sixtFocusmodeState ? 'default' : 'highlighted'}
              trigger={<div></div>}
            />
          </ToggleWrapper>
        </>
      )}
    </>
  );
};
